import { Stack } from "@mui/material";
import { useTheme } from "design-system/themeContext";
import { IfcExecutionPanel } from "features/dt-plus-setup/components/IfcExecutionPanel";
import { ExecutionNavMenu } from "features/execution/components/ExecutionNavMenu";
import { EXECUTION_TABS, type ExecutionTab } from "features/execution/model/constants";
import { TransientResponsePanel } from "features/transient-response/components/TransientResponsePanel";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { IsAllowed } from "../acl";
import { resources } from "../acl/resources";
import { eventNames, trackGAEvent } from "../functions/googleAnalytics";
import useQueryParamsState from "../hooks/states";

const isExecutionTab = (value: string): value is ExecutionTab =>
  value === EXECUTION_TABS.ifc || value === EXECUTION_TABS.transientResponse;

export const Execution = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [activeTabParam, setActiveTabParam] = useQueryParamsState("tab", EXECUTION_TABS.ifc);
  const activeTab = isExecutionTab(activeTabParam) ? activeTabParam : EXECUTION_TABS.ifc;
  const canView = IsAllowed(resources.EXECUTION.VIEW);
  const canRun = IsAllowed(resources.EXECUTION.RUN);

  useEffect(() => {
    if (canView) trackGAEvent(eventNames.EXECUTION_VIEW);
  }, [canView]);

  if (!canView) return <Navigate replace to="/" />;

  return (
    <>
      <Helmet>
        <title>
          {t("Execution.pageTitle")} | {theme.clientDisplayName}
        </title>
      </Helmet>
      <Stack className="mt-4 flex-row items-start gap-6">
        <ExecutionNavMenu
          activeTab={activeTab}
          className="sticky top-4 h-[calc(100vh-10rem)] w-56 shrink-0"
          setActiveTab={(tab) => setActiveTabParam(tab)}
        />
        <div className="min-w-0 flex-1">
          {activeTab === EXECUTION_TABS.ifc && <IfcExecutionPanel canRun={canRun} canView={canView} />}
          {activeTab === EXECUTION_TABS.transientResponse && <TransientResponsePanel />}
        </div>
      </Stack>
    </>
  );
};
